import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { OktaService } from './okta.service';
import { environment } from '../../../environments/environment';
import { HcToastOptions, HcToasterService } from '@healthcatalyst/cashmere';
    
export interface TermChangeElement {
    'id': number | null,
    'submitDate': Date,
    'publishDate': Date | null,
    'termId': number | null,
    'name': string,
    'usage': string,
    'comment': string,
    'contributorName': string,
    'contributorEmail': string
}

@Injectable()
export class TermChangeService {
    termChangeList:BehaviorSubject<TermChangeElement[] | null> = new BehaviorSubject<TermChangeElement[] | null>(null);
    loadFailed = false;

    constructor( private http: HttpClient, private oktaService: OktaService, private toasterService: HcToasterService ){};

    get termChangesUpdated(): Observable<TermChangeElement[] | null> {
        return this.termChangeList.asObservable();
    }

    refresh() {
        this.loadFailed = false;

        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        headers = headers.append('Authorization', `Bearer ${this.oktaService.accessToken}`);
        const options = {headers: headers};

        this.http.get<any>( environment.azure.url + '/admin.termchange', options)
            .subscribe({
                next: data => {
                    let loadedTerms: TermChangeElement[] = [];
                    data.forEach((term:any) => {
                        loadedTerms.push({
                            id: term.termChangeID,
                            submitDate: new Date(term.submitDate),
                            publishDate: term.publishDate ? new Date(term.publishDate) : null,
                            termId: term.term,
                            name: term.name,
                            usage: term.usage,
                            comment: term.comment,
                            contributorName: term.contributorName,
                            contributorEmail: term.contributorEmail
                        });
                    });
                    this.termChangeList.next ( loadedTerms );
                    this.loadFailed = false;
                },
                error: msg => { 
                    this.loadFailed = true;
                    this.termChangeList.next(null);
                    console.log( msg );
                }
            });
    }

    add( target: TermChangeElement ) {
        // Write target to database
        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        const options = {headers: headers};

        let postSub = this.http.post<any>( environment.azure.url + '/termchange.add',
            {
                submitDate: target.submitDate.toISOString(),
                publishDate: target.publishDate ? target.publishDate.toISOString() : null,
                term: target.termId,
                name: target.name,
                usage: target.usage,
                comment: target.comment,
                contributorName: target.contributorName,
                contributorEmail: target.contributorEmail
            },
            options
        )
        .subscribe({
            next: () => {
                this.refresh();
                const options: HcToastOptions = {
                    header: 'Term Change Added',
                    body: target.name + " successfully added.",
                    type: 'success',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                postSub.unsubscribe();
            },
            error: msg => {
                const options: HcToastOptions = {
                    header: 'Add Failed',
                    body: "Unable to add term change. Try again later.",
                    type: 'alert',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                console.log( msg );
                postSub.unsubscribe();
            }
        });
    }

    update( target: TermChangeElement ) {
        // Update target in database
        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        headers = headers.append('Authorization', `Bearer ${this.oktaService.accessToken}`);
        const options = {headers: headers};

        let postSub = this.http.post<any>( environment.azure.url + '/admin.termchange.update',
            {
                termChangeID: target.id,
                submitDate: target.submitDate.toISOString(),
                publishDate: target.publishDate ? target.publishDate.toISOString() : null,
                term: target.termId,
                name: target.name,
                usage: target.usage,
                comment: target.comment,
                contributorName: target.contributorName,
                contributorEmail: target.contributorEmail
            },
            options
        )
        .subscribe({
            next: () => {
                this.refresh();
                const options: HcToastOptions = {
                    header: 'Term Change Updated',
                    body: target.name + " successfully updated.",
                    type: 'success',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                postSub.unsubscribe();
            },
            error: msg => {
                const options: HcToastOptions = {
                    header: 'Update Failed',
                    body: "Unable to update term change. Try again later.",
                    type: 'alert',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                console.log( msg );
                postSub.unsubscribe();
            }
        });
    }
}
